import {
  IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED,
  USE_RECORDING_V2,
} from '@/app-shell/flags/capture-flags';
import { offlineCaptureRunningAtom } from '@/atoms/offline-recording';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { activeSessionIdAtom, notFoundSessionIds } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import Router from 'next/router';
import { useEffect } from 'react';

/**
 * Matches both session route patterns:
 *   /scribe/session/:id   — legacy single-session route
 *   /scribe/sessions/:id  — new session-list split-panel route
 *
 * If a new session route pattern is added, update this regex.
 */
const SESSION_PATH_REGEX = /\/scribe\/sessions?\/([^/?#]+)/;

/**
 * Global navigation guard that prevents switching to a different session
 * while a recording is active. Allows all other navigation freely.
 *
 * Uses activeSessionIdAtom as the source of truth — active when a session
 * is recording, cleared on pause or stop.
 */
export const useBlockNextNavigationGlobal = () => {
  const { t } = useTranslations();
  const activeSessionId = useAtomValue(activeSessionIdAtom);
  // An active offline capture must block cross-session navigation too, for
  // parity with online — but the online guard is gated on the live-transcription
  // indicator flag, which is off on the offline path. `offlineCaptureRunningAtom`
  // is true only while recording (not paused), so paused offline captures stay
  // navigable exactly like paused online ones.
  const isOfflineCaptureRunning = useAtomValue(offlineCaptureRunningAtom);

  useEffect(() => {
    if (!IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED() && !isOfflineCaptureRunning) return;
    if (!activeSessionId) return;

    const nativeBrowserHandler = (event: BeforeUnloadEvent) => {
      if (!isTauri()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    const nextNavigationHandler = (url: string) => {
      const match = url.match(SESSION_PATH_REGEX);
      const targetSessionId = match?.[1];
      const isSameSession = targetSessionId === activeSessionId;
      const wasBlocked = !!targetSessionId && !isSameSession;

      if (!isSameSession) {
        track({
          event_name: TrackingEventName.ActiveSessionNavigation,
          props: {
            session_id: activeSessionId,
            from_path: Router.pathname,
            to_path: url,
            was_blocked: wasBlocked,
          },
        });
      }

      if (wasBlocked) {
        // The atom is set optimistically on start, before the timer ticks, so
        // an idle timer at this point means navigation was blocked with no
        // recording actually in progress — a leaked atom, not a real block.
        // Logged here (not on every write) so the signal is free of the
        // legitimate transient during start-up.
        if (!store.getState().timerModel.isRunning) {
          logger.warn('NAV_BLOCKED_WITHOUT_ACTIVE_RECORDING', {
            active_session_id: activeSessionId,
            to_path: url,
            timer_seconds: store.getState().timerModel.seconds,
          });
        }
        Router.events.emit('routeChangeError');
        toast({
          tone: 'destructive',
          title: t('navigation.recordingInProgress'),
          description: t('navigation.recordingInProgressDescription'),
        });
        throw new Error("Abort route change by user's confirmation.");
      }
    };

    window.addEventListener('beforeunload', nativeBrowserHandler);
    Router.events.on('beforeHistoryChange', nextNavigationHandler);

    return () => {
      window.removeEventListener('beforeunload', nativeBrowserHandler);
      Router.events.off('beforeHistoryChange', nextNavigationHandler);
    };
  }, [activeSessionId, isOfflineCaptureRunning, t]);

  // Observability hook for the "timer running with null activeSessionIdAtom"
  // desync, where the singleton Redux timer keeps ticking after the atom that
  // gates the navigation block has been cleared. When the user navigates
  // mid-recording in that state, this log records the path + flag combo so
  // future debugging has a concrete signal instead of guessing across the
  // ~7 timer-start sites and ~3 atom writers.
  useEffect(() => {
    const leakDetector = () => {
      if (activeSessionId) return;
      if (!store.getState().timerModel.isRunning) return;
      logger.warn('RECORDING_ATOM_LEAK: timer running with no active session', {
        pathname: Router.pathname,
        asPath: Router.asPath,
        flag_indicator: IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED(),
        flag_recording_v2: USE_RECORDING_V2(),
        not_found_set_size: notFoundSessionIds.size(),
      });
    };
    Router.events.on('beforeHistoryChange', leakDetector);
    return () => Router.events.off('beforeHistoryChange', leakDetector);
  }, [activeSessionId]);
};
